import { useRef } from "react";
import { motion } from "framer-motion";
import { resolveImage } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function WhyMickey({ d }) {
  const accent = "#D4AF37";

  return (
    <section id="why_mickey" className="bg-[#0A0A0A] relative overflow-hidden" data-testid="section-why-mickey">

      {/* ─── OPENING QUESTION ─── */}
      <div className="py-28 md:py-36 px-6 text-center relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 50% 40% at 50% 50%, rgba(212,175,55,0.03) 0%, transparent 100%)" }} />
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeUp} className="max-w-3xl mx-auto relative z-10">
          <h2 className="font-mc-h text-4xl md:text-6xl font-light text-[#F5F5F0] mb-8 leading-tight">{d.why_mickey_opening_q}</h2>
          <div className="font-mc-h text-lg md:text-xl text-zinc-400 font-light leading-[1.9] whitespace-pre-line">{d.why_mickey_opening_a}</div>
        </motion.div>
      </div>

      {/* ─── WHY MICKEY + PHOTO ─── */}
      <div className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Photo */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-5 relative">
            <div className="relative">
              {/* Gold glow behind photo */}
              <div className="absolute -inset-8 rounded-full pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(212,175,55,0.08) 0%, transparent 70%)", filter: "blur(30px)" }} />
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl">
                <img src={resolveImage(d.why_mickey_image || d.about_image)} alt="Mickey Trivett" className="w-full h-full object-cover" data-testid="why-mickey-image" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-2xl" />
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-7">
            <p className="font-mc-b text-xs tracking-[0.3em] uppercase mb-6" style={{ color: accent }}>Why Mickey?</p>
            <div className="font-mc-b text-base text-zinc-300 leading-[1.9] whitespace-pre-line">{d.why_mickey_main_copy}</div>
          </motion.div>
        </div>
      </div>

      {/* ─── BUILT THROUGH REAL LIFE ─── */}
      <div className="py-20 md:py-28 px-6 bg-[#0D0D0D]">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <div className="gold-divider max-w-xs mx-auto mb-8" />
            <h3 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0]">{d.why_mickey_built_heading}</h3>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="font-mc-b text-base text-zinc-300 leading-[1.9] whitespace-pre-line text-center max-w-3xl mx-auto">{d.why_mickey_built_copy}</div>
          </motion.div>
        </div>
      </div>

      {/* ─── MY BACKGROUND (Credentials) ─── */}
      <div className="py-20 md:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <h3 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0]">{d.why_mickey_bg_heading}</h3>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {d.why_mickey_credentials?.map((cred, i) => (
              <motion.div key={i} variants={fadeUp} className="rounded-xl p-5 border border-[#D4AF37]/15 bg-[#D4AF37]/[0.03] text-center hover:border-[#D4AF37]/30 transition-colors" data-testid={`credential-${i}`}>
                <div className="w-1.5 h-1.5 rounded-full mx-auto mb-3" style={{ background: accent }} />
                <p className="font-mc-b text-sm text-[#F5F5F0] leading-relaxed">{cred}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="font-mc-b text-base text-zinc-300 leading-[1.9] whitespace-pre-line text-center max-w-3xl mx-auto">{d.why_mickey_bg_copy}</div>
          </motion.div>
        </div>
      </div>

      {/* ─── WHY MC EXISTS ─── */}
      <div className="py-20 md:py-28 px-6 bg-[#0D0D0D] relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(212,175,55,0.02) 0%, transparent 100%)" }} />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="gold-divider max-w-xs mx-auto mb-12" />
            <h3 className="font-mc-h text-3xl md:text-4xl font-light text-[#F5F5F0] mb-10">{d.why_mickey_exists_heading}</h3>
            <div className="font-mc-b text-base text-zinc-300 leading-[1.9] whitespace-pre-line">{d.why_mickey_exists_copy}</div>
          </motion.div>
        </div>
      </div>

      {/* ─── MY PROMISE ─── */}
      <div className="py-28 md:py-36 px-6 relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 40% 40% at 50% 50%, rgba(212,175,55,0.04) 0%, transparent 100%)" }} />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mc-b text-xs tracking-[0.3em] uppercase mb-6" style={{ color: accent }}>A Personal Commitment</p>
            <h3 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-10">{d.why_mickey_promise_heading}</h3>
            <div className="font-mc-h text-lg md:text-xl text-zinc-300 font-light leading-[1.9] whitespace-pre-line mb-14">{d.why_mickey_promise_copy}</div>

            <a href={d.why_mickey_btn_target || "#packages"} className="inline-block border px-12 py-5 rounded-full font-mc-b tracking-wide uppercase text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)]" style={{ borderColor: accent, color: accent }} data-testid="why-mickey-cta">
              {d.why_mickey_btn_text || "Take a Seat"}
            </a>
            {d.why_mickey_btn_subtext && (
              <p className="font-mc-b text-sm text-zinc-600 mt-4 italic">{d.why_mickey_btn_subtext}</p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
